import {
  registerBrick,
  Query,
  Order,
  CloudObject,
  User,
  Config,
  Auth,
} from 'olympe';
import { useEffect, useState, useCallback } from 'react';
import { getLogger, ReactBrick, useProperty } from '@olympeio/core';
import {
  getDataGridRenderer,
  getDataGridSorterer,
} from './DataGridRegistry.jsx';
import DataGridConfig, {
  getValuesFromPaths,
  getPropertiesFromPaths,
} from './DataGridConfig';
import { combineLatest } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import * as React from 'react';
import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import Icon from '@mui/material/Icon';
import Tooltip from '@mui/material/Tooltip';
import { LicenseInfo } from '@mui/x-license-pro';
import BCP from '../../../common/section_bcp/DM_BCP.js';
import { getApplicationContext } from '../../../common/utils/Context';

export default class EnhancedDataGrid extends ReactBrick {
  /**
   * @override
   */
  init($) {
    // License key for DataGrid Pro
    LicenseInfo.setLicenseKey(
      Config.getParameter('uiAppValues.datagrid_license')
    );

    // Initialize columns (config) and rows (data)
    this.initConfig($);
    this.initData($);

    // Initialize output properties
    $.set('Selected Objects', []);
    $.set('Action Clicked', '');
    $.set('Actioned Object', null);
    $.set('Current Page', 0);
    $.set('Clicked Object', null);
  }

  /**
   * @override
   */

  static getReactComponent($) {
    let logger2 = getLogger('EnhanceDataGrid: getReactComponent');
    return () => {
      // Properties
      const width = $.get('Width') || 1000;
      const height = $.get('Height') || 800;
      const columns = useProperty($, 'Columns');
      const rows = useProperty($, 'Rows');
      const rowsPerPage = useProperty($, 'Rows Per Page');
      const rowCount = useProperty($, 'Row Count') || rows?.length;
      const currentPage = useProperty($, 'Current Page');
      const loading = useProperty($, 'Loading');
      const checkboxSelection = useProperty($, 'Checkbox Selection');
      const mode = useProperty($, 'Mode');
      const currentUser = CloudObject.get(Auth.getCurrentUser()).get(
        User.loginProp
      );
      const selectedRows = useProperty($, 'Selected Rows');
      const pinnedCols = useProperty($, 'pinnedColumns') || {
        left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
        right: [],
      };

      const handleOnRowsScrollEnd = (params) => {
        if (rows.length > 0) {
          $.trigger('On Scroll Trigger');
        }
      };

      //Add indicator for dataGrid Row
      const addIndicator = (params) => {
        try {
          const requestor = CloudObject.get(params)
            .get(BCP.requestorProp)
            .split(';')[1];
          const localSession = localStorage.getItem('databcp');
          if (localSession === null) return '';
          else
            return localSession.includes(params) || requestor === currentUser
              ? ''
              : 'indicatorSt';
        } catch (error) {
          console.error('deleted row object tag not in cloud object', error);
        }
      };

      const [displayDG, setDisplayDG] = useState(false);

      useEffect(() => {
        setDisplayDG(width && height && columns);
      }, [width, height, columns]);

      const handlePinnedColumnsChange = useCallback((updatedPinnedColumns) => {
        $.set('pinnedColumns', updatedPinnedColumns);
      }, []);

      // Check mandatory properties & rendering
      return displayDG ? (
        <Box sx={{ display: 'flex', width: 1, height: 1 }}>
          <Box sx={{ width: Math.max(width, 960), height: 1, flexGrow: 1 }}>
            <DataGridPro
              // Config & Data
              sx={{
                '& .indicatorSt': {
                  backgroundColor: 'lightyellow',
                },
              }}
              columns={columns}
              // we need rows only because this value is OBSERVED
              // otherwise deletion of items goes unnoticed when removed from the data
              // "Data" property of the BCP data grid.
              rows={rows}
              pagination={false}
              paginationMode={mode}
              page={currentPage}
              pageSize={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
              rowCount={rowCount}
              loading={loading}
              onPageChange={(page) => {
                $.set('Current Page', page);
                $.trigger('On Page Change');
              }}
              // Selection
              checkboxSelection={checkboxSelection}
              disableRowSelectionOnClick={true}
              disableSelectionOnClick={true}
              selectionModel={selectedRows || []}
              onSelectionModelChange={(selection) => {
                $.set('Selected Objects', selection.map(CloudObject.get));
                const applicationContext = getApplicationContext($);
                applicationContext.set(
                  'selectedBCPs',
                  selection.map(CloudObject.get)
                );
                logger2.info('selected BCPs:', selection.map(CloudObject.get));
                $.set('Selected Rows', [...selection]);
                $.trigger('On Selection Change');
              }}
              getRowClassName={(params) => addIndicator(params.id)}
              onRowsScrollEnd={handleOnRowsScrollEnd}
              getRowId={(row) => row.id}
              scrollEndThreshold={1}
              pinnedColumns={pinnedCols}
              onPinnedColumnsChange={handlePinnedColumnsChange}
            />
          </Box>
        </Box>
      ) : null;
    };
  }

  /**
   * @private
   * @param {!BrickContext} $
   */
  initConfig($) {
    const logger = getLogger('Enhanced DataGrid init');
    // Default value
    $.set('Columns', []);
    $.set('Cell Elements', new Map()); // memoization
    // Observe config type
    const configObserver = $.observe('Config').pipe(
      // Observe columns config
      switchMap((configType) =>
        Query.instancesOf(configType)
          .andReturn()
          .sortBy(DataGridConfig.columnPositionProp, Order.ASC)
          .observe($)
      ),
      // Observe each column properties
      switchMap((config) =>
        combineLatest(config.map((column) => column.observeObject($)))
      )
    );
    const cleanCachObserver = $.observe('Clean Caching');
    combineLatest([configObserver, cleanCachObserver]).subscribe(
      ([columns, _]) => {
        // Reset elements
        const elementsCache = $.get('Cell Elements');
        const pinnedColumns = {
          left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
          right: [],
        };
        elementsCache.clear();
        $.set(
          'Columns',
          columns.map((column) => {
            const columnName = column[DataGridConfig.columnNameProp.getTag()];
            const columnOptions =
              column[DataGridConfig.columnOptionsProp.getTag()];
            const rendererOptions =
              column[DataGridConfig.rendererOptionsProp.getTag()];
            const valuePaths = column[DataGridConfig.valuePathsProp.getTag()];
            const sortable = column[DataGridConfig.sortableProp.getTag()];
            const isColumnEditable = rendererOptions?.editable ?? false;
            const isPinned = column[DataGridConfig.pinnedProp.getTag()];
            const pinnedPosition =
              column[DataGridConfig.pinPositionProp.getTag()];
            const renderer = getDataGridRenderer(
              column[DataGridConfig.rendererTypeProp.getTag()]
            );
            const deleteElementsForColumns = (cell, cols) => {
              for (const col of cols) {
                // invalid the rest of the row
                const keyToDelete = `${col.tag}.${cell.row.id}`;
                elementsCache.delete(keyToDelete);
              }
            };

            /**
             * sort function to sort Data Grid Columns,this method is executed from sort comparator
             * sort function consist of 4 parameters,param1 and param2 is undefined
             * we use param3 and param4 to fetch the value of cloud object that we need to compare the rows in datagrid
             */
            const sort = (param1, param2, param3, param4) => {
              const obj1 = CloudObject.get(param3.id);
              const obj2 = CloudObject.get(param4.id);
              const path = CloudObject.get(param3.field).get(
                DataGridConfig.valuePathsProp
              );

              //fetching sorterer type like number,text,date,BCPworkflow status
              const sortererType = CloudObject.get(param3.field).get(
                DataGridConfig.sortererTypeProp
              );

              //fetching the array by calling getValuesFromPaths it has two params cloud obj and path
              const array1 = getValuesFromPaths(obj1, path);
              const array2 = getValuesFromPaths(obj2, path);

              //values to compare
              const value1 = array1[0];
              const value2 = array2[0];
              //Return a function which takes a type of sorterer based on that it will call a sort funtion
              return getDataGridSorterer(sortererType).sort(
                value1,
                value2,
                $,
                obj1,
                obj2
              );
            };

            if (isPinned) {
              pinnedColumns[pinnedPosition] = [
                ...pinnedColumns[pinnedPosition],
                column.tag,
              ];
            }
            return {
              // Field
              field: column.tag,
              headerName: columnName,

              // Config
              editable: isColumnEditable,
              filterable: false,
              sortable: sortable,
              hideable: !!columnName,
              flex: !!columnOptions?.width ? null : 1,

              // User Config
              ...columnOptions,

              // Header rendering
              renderHeader: columnOptions?.icon
                ? () => (
                    <Tooltip title={columnName}>
                      <Icon>{columnOptions.icon}</Icon>
                    </Tooltip>
                  )
                : null,

              // Rendering
              renderCell: (cell) => {
                const setNewCellValue = (newValue, apiRef) => {
                  // This is called when any update happens by newCellValue
                  apiRef.current.updateRows([
                    { id: cell.row.id, [column.tag]: newValue },
                  ]);
                };
                const key = `${column.tag}.${cell.row.id}`;
                let element = elementsCache.get(key);
                if (!element) {
                  const values = getValuesFromPaths(
                    cell.row.object,
                    valuePaths
                  );
                  element = renderer.render(
                    values,
                    rendererOptions,
                    cell.row.object,
                    $,
                    column,
                    deleteElementsForColumns.bind(this, cell, columns),
                    setNewCellValue.bind(this)
                  );
                  if ($.get('Enable Cache')) {
                    elementsCache.set(key, element);
                  }
                }
                return element;
              },
              sortComparator: (a, b, c, d) => sort(a, b, c, d),
              renderEditCell: (cell) => {
                const key = `${column.tag}.${cell.row.id}`;
                let element;
                const property = getPropertiesFromPaths(
                  cell.row.object,
                  valuePaths
                )[0];
                if (
                  typeof renderer.renderForEdition === 'function' &&
                  property
                ) {
                  elementsCache.delete(key);
                  const value = getValuesFromPaths(
                    cell.row.object,
                    valuePaths
                  )[0];
                  element = renderer.renderForEdition(
                    value,
                    cell.row.object,
                    property,
                    $,
                    rendererOptions,
                    column,
                    deleteElementsForColumns.bind(this, cell, columns)
                  );
                } else {
                  logger.error(
                    `Configuration error, On ${valuePaths} prop. Check the type on DataGrid config for the renderForEdition. ${cell.row.object}`
                  );
                  element = elementsCache.get(key);
                }
                return element;
              },
            };
          })
        );
        $.set('pinnedColumns', pinnedColumns);
      }
    );
  }

  /**
   * @private
   * @param {!BrickContext} $
   */
  initData($) {
    // Default value
    $.set('Rows', []);
    //   Observe data
    $.observe('Data').subscribe((data) => {
      $.set(
        'Rows',
        data.map((object) => ({
          id: object.getTag(),
          object: object,
        }))
      );
    });
  }
}

registerBrick('018857f08e92eec07511', EnhancedDataGrid);
